import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUpdateExternalApp } from "../../../modules/private/settings-module/integrations/apps/hooks/useUpdateExternalApp";
import Api from "../../../util/api";
import { ExternalAppApiType, QueryKeys } from "../../../util/types";
import { TagManagerEntityTypes } from "../index";

export const useAddTagToEntity = () => {
  const queryClient = useQueryClient();
  const { updateExternalApp } = useUpdateExternalApp();

  const { mutateAsync: addTagToEntity, isPending: isLoading } = useMutation({
    mutationKey: [QueryKeys.REMOVE_TAG_FROM_ENTITY],
    mutationFn: async ({
      entityId,
      entityType,
      tag,
      appData,
    }: {
      entityId: string;
      entityType: TagManagerEntityTypes;
      tag: string;
      errorCallback: (error?: string) => void;
      appData?: ExternalAppApiType;
    }) => {
      if (entityType === TagManagerEntityTypes.ORDER) {
        return await Api.addOrderTag(entityId, tag);
      } else if (entityType === TagManagerEntityTypes.CUSTOMER) {
        return await Api.addCustomerTag(entityId, tag);
      } else if (entityType === TagManagerEntityTypes.APP_ORDER_TAGS) {
        if (!appData) return;
        return await updateExternalApp({
          id: entityId,
          data: {
            wholesaleOrderTags: [
              ...(appData.wholesaleOrderTags ?? []),
              tag.trim().toLowerCase(),
            ],
          },
        });
      } else if (entityType === TagManagerEntityTypes.APP_CUSTOMER_TAGS) {
        if (!appData) return;
        return await updateExternalApp({
          id: entityId,
          data: {
            wholesaleCustomerTags: [
              ...(appData.wholesaleCustomerTags ?? []),
              tag.trim().toLowerCase(),
            ],
          },
        });
      }
    },
    onSuccess: async (_, { entityId, entityType }) => {
      if (entityType === TagManagerEntityTypes.ORDER) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.GET_ORDER_DETAILS, entityId],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.ADVANCED_SEARCH_ORDER_TAGS_LIST],
        });
      } else if (entityType === TagManagerEntityTypes.CUSTOMER) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.GET_CUSTOMER, entityId],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.ADVANCED_SEARCH_CUSTOMER_TAGS_LIST],
        });
      } else if (entityType === TagManagerEntityTypes.APP_ORDER_TAGS) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.GET_EXTERNAL_APP, entityId],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.ADVANCED_SEARCH_ORDER_TAGS_LIST],
        });
      } else if (entityType === TagManagerEntityTypes.APP_CUSTOMER_TAGS) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.GET_EXTERNAL_APP, entityId],
        });
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.ADVANCED_SEARCH_CUSTOMER_TAGS_LIST],
        });
      }
    },
    onError: async (_, { errorCallback }) => {
      errorCallback("Failed to add tag");
    },
  });

  return { addTagToEntity, isLoading };
};
